import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const ThemeContext = createContext(null);

const THEME_KEY = 'partyresume_theme';

// Theme definitions
export const THEMES = {
  outside: {
    name: 'outside',
    label: 'Outside',
    description: 'Clean, minimalist black and white design for public-facing views',
    colors: {
      primary: '#000000',
      secondary: '#ffffff',
      accent: '#666666',
      background: '#ffffff',
      surface: '#f5f5f5',
      text: '#000000',
      textSecondary: '#666666',
      border: '#e0e0e0',
      error: '#d32f2f',
      success: '#388e3c',
      warning: '#f57c00',
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      headingWeight: 600,
      bodyWeight: 400,
    },
    spacing: {
      density: 'comfortable',
      scale: 1,
    },
  },
  inside: {
    name: 'inside',
    label: 'Inside',
    description: 'Dense, operational interface for authenticated users',
    colors: {
      primary: '#1a1a2e',
      secondary: '#16213e',
      accent: '#0f3460',
      background: '#0f0f1a',
      surface: '#1a1a2e',
      text: '#e0e0e0',
      textSecondary: '#a0a0a0',
      border: '#2a2a4a',
      error: '#ef5350',
      success: '#66bb6a',
      warning: '#ffa726',
      highlight: '#e94560',
    },
    typography: {
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      headingWeight: 700,
      bodyWeight: 400,
    },
    spacing: {
      density: 'compact',
      scale: 0.875,
    },
  },
};

export function ThemeProvider({ children }) {
  // Initialize theme from localStorage or default to 'outside'
  const [themeName, setThemeName] = useState(() => {
    const stored = localStorage.getItem(THEME_KEY);
    return stored && THEMES[stored] ? stored : 'outside';
  });

  // Get current theme object
  const theme = useMemo(() => THEMES[themeName], [themeName]);

  // Check if using inside theme
  const isInsideTheme = themeName === 'inside';
  const isOutsideTheme = themeName === 'outside';

  // Set theme and persist to localStorage
  const setTheme = useCallback((newTheme) => {
    if (THEMES[newTheme]) {
      setThemeName(newTheme);
      localStorage.setItem(THEME_KEY, newTheme);
    } else {
      console.warn(`Invalid theme: ${newTheme}`);
    }
  }, []);

  // Toggle between outside and inside themes
  const toggleTheme = useCallback(() => {
    const newTheme = themeName === 'outside' ? 'inside' : 'outside';
    setTheme(newTheme);
  }, [themeName, setTheme]);

  // Apply CSS custom properties when theme changes
  useEffect(() => {
    const root = document.documentElement;
    const currentTheme = THEMES[themeName];

    // Apply color variables
    Object.entries(currentTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Apply typography variables
    root.style.setProperty('--font-family', currentTheme.typography.fontFamily);
    root.style.setProperty('--heading-weight', currentTheme.typography.headingWeight);
    root.style.setProperty('--body-weight', currentTheme.typography.bodyWeight);

    // Apply spacing variables
    root.style.setProperty('--density', currentTheme.spacing.density);
    root.style.setProperty('--spacing-scale', currentTheme.spacing.scale);

    // Set theme attribute on body for CSS targeting
    document.body.setAttribute('data-theme', themeName);

    // Apply background color to body
    document.body.style.backgroundColor = currentTheme.colors.background;
    document.body.style.color = currentTheme.colors.text;
  }, [themeName]);

  // Get a specific color from current theme
  const getColor = useCallback((colorName) => {
    return theme.colors[colorName] || null;
  }, [theme]);

  // Get CSS variable reference for a color
  const getCSSVar = useCallback((colorName) => {
    return `var(--color-${colorName})`;
  }, []);

  const value = {
    theme,
    themeName,
    isInsideTheme,
    isOutsideTheme,
    setTheme,
    toggleTheme,
    getColor,
    getCSSVar,
    availableThemes: Object.keys(THEMES),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
